import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { Badge, Button, Icon, IconButton, Segmented, ToastProvider, useThemePreference } from '@agency/ui';
import { useMediaQuery } from './lib/use-media-query.ts';
import { NAV } from './nav.ts';
import { useAuth } from './lib/auth.tsx';
import { CommandPalette } from './command-palette.tsx';

export function Shell() {
  const { user, can, signOut } = useAuth();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('agency-nav-collapsed') === '1');
  const [mobileNav, setMobileNav] = useState(false);
  const [palette, setPalette] = useState(false);
  const [theme, setTheme] = useThemePreference();
  const nav = useNavigate();
  const loc = useLocation();
  const items = useMemo(() => NAV.filter((n) => can(n.perm) && (!n.extraPerm || can(n.extraPerm))), [can]);
  const groups = [...new Set(items.map((i) => i.group))];
  const pending = useRef<string | null>(null);
  const contentRef = useRef<HTMLElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('(max-width: 719px)');

  // Route changes: update the document title and move focus to the main region so keyboard and screen-reader users land on the new page.
  const first = useRef(true);
  useEffect(() => {
    const label = NAV.find((n) => loc.pathname.startsWith(n.to))?.label ?? (loc.pathname === '/account' ? 'Account security' : loc.pathname === '/design-review' ? 'Design review' : 'Admin');
    document.title = `${label} · Agency admin`;
    if (first.current) { first.current = false; return; }
    requestAnimationFrame(() => contentRef.current?.focus({ preventScroll: true }));
  }, [loc.pathname]);

  // Mobile navigation sheet: focus in on open, Escape closes, focus returns to the menu button.
  const menuBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!mobileNav) return;
    sidebarRef.current?.querySelector<HTMLElement>('a')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setMobileNav(false); menuBtn.current?.focus(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileNav]);

  useEffect(() => localStorage.setItem('agency-nav-collapsed', collapsed ? '1' : '0'), [collapsed]);
  useEffect(() => setMobileNav(false), [loc.pathname]);

  // Shortcuts never fire while typing, and never swallow modified keys.
  useEffect(() => {
    let timer: number | undefined;
    const typing = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      return !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName) || el.closest('[role="dialog"]') !== null);
    };
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPalette((p) => !p); return; }
      if (e.metaKey || e.ctrlKey || e.altKey || typing(e.target)) return;
      if (pending.current === 'g') {
        const hit = items.find((i) => i.keys === `g ${e.key.toLowerCase()}`);
        pending.current = null;
        if (hit) { e.preventDefault(); nav(hit.to); }
        return;
      }
      if (e.key === 'g') { pending.current = 'g'; window.clearTimeout(timer); timer = window.setTimeout(() => (pending.current = null), 1200); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, nav]);

  return (
    <ToastProvider>
      <a className="ag-skip" href="#content">Skip to content</a>
      <div className="shell" data-collapsed={collapsed || undefined} data-mobile-open={mobileNav || undefined}>
        <div className="sidebar" ref={sidebarRef} inert={isMobile && !mobileNav ? true : undefined}>
          <div className="sidebar__brand">
            <span className="sidebar__mark" aria-hidden="true">A</span>
            <span className="sidebar__name">Agency admin</span>
            <IconButton className="sidebar__close" icon="x" label="Close navigation" onClick={() => { setMobileNav(false); menuBtn.current?.focus(); }} />
          </div>
          <nav aria-label="Main">
            {groups.map((g) => (
              <div key={g} className="navgroup">
                <p className="navgroup__label">{g}</p>
                {items.filter((i) => i.group === g).map((i) => (
                  <NavLink key={i.to} to={i.to} className="navitem" aria-label={i.label} title={collapsed ? i.label : undefined}>
                    <Icon name={i.icon} size={20} />
                    <span className="navitem__text">{i.label}</span>
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
          <div className="sidebar__mobile">
            <p><Link to="/account"><strong>{user?.name}</strong></Link><br /><span>{user?.roleName}</span></p>
            <Button size="sm" icon="logout" onClick={() => void signOut()}>Sign out</Button>
          </div>
          <div className="sidebar__foot">
            <IconButton icon={collapsed ? 'chevronRight' : 'chevronLeft'} label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} onClick={() => setCollapsed((c) => !c)} />
          </div>
        </div>
        {mobileNav ? <div className="shell__scrim" onClick={() => setMobileNav(false)} aria-hidden="true" /> : null}
        <div className="shell__main">
          <header className="topbar">
            <button ref={menuBtn} type="button" className="ag-iconbtn topbar__menu" aria-label="Open navigation" aria-expanded={mobileNav} onClick={() => setMobileNav(true)}><Icon name="menu" size={16} /></button>
            <button type="button" className="topbar__search" onClick={() => setPalette(true)} aria-keyshortcuts="Control+K Meta+K">
              <Icon name="search" />
              <span>Search or jump to…</span>
              <kbd>⌘K</kbd>
            </button>
            <div className="topbar__right">
              <Segmented label="Theme" value={theme} onChange={setTheme} options={[{ id: 'light', label: 'Light theme', icon: 'sun' }, { id: 'system', label: 'System theme', icon: 'monitor' }, { id: 'dark', label: 'Dark theme', icon: 'moon' }]} />
              <div className="topbar__user">
                <Link className="topbar__who" to="/account" aria-label={`${user?.name}: account security`}>{user?.name}</Link>
                {user?.name !== user?.roleName ? <Badge>{user?.roleName}</Badge> : null}
              </div>
              <Button size="sm" variant="ghost" icon="logout" onClick={() => void signOut()}>Sign out</Button>
            </div>
          </header>
          <main id="content" className="content" tabIndex={-1} ref={contentRef}>
            <Outlet />
          </main>
        </div>
      </div>
      <CommandPalette open={palette} onClose={() => setPalette(false)} items={items} />
    </ToastProvider>
  );
}
